import React, { useEffect, useMemo, useState } from 'react'
import {
    Table,
    CardBody,
    Accordion,
    AccordionItem,
    AccordionHeader,
    AccordionBody,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Label,
    Input
} from 'reactstrap'
import { API_URL } from './utils/Constants/URLS'
import { useParams } from 'react-router-dom'

// Helper: format money
const money = n =>
    (Number.isFinite(+n) ? Number(n) : 0).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })

// Derive buyerPos (state code) from GSTIN (first 2 characters)
const getBuyerPos = gstin =>
    typeof gstin === 'string' && gstin.length >= 2 ? gstin.slice(0, 2) : ''

// Helper to derive consistent pick-list id
const getPickListId = (pl, idx) => String(pl._id ?? pl.id ?? `pl-${idx}`)

const FocInvoice = () => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null
    const { storeid } = useParams()

    const [packListData, setPackListData] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [pageNumber, setPageNumber] = useState(1)

    const [openId, setOpenId] = useState('')
    const [btnLoading, setBtnLoading] = useState(false)
    const [creatingId, setCreatingId] = useState(null)
    const [showDeclineModal, setShowDeclineModal] = useState(false)
    const [plId, setPlId] = useState('')
    const [rejectionReason, setRejectionReason] = useState('')

    // Store invoice form data per pick-list id
    const [invoiceForm, setInvoiceForm] = useState({})

    // edited narrations per pick-list → line item
    const [editedNarrations, setEditedNarrations] = useState({})

    const handleCloseDeclineModal = () => {
        setShowDeclineModal(false)
        setPlId('')
        setRejectionReason('')
    }

    const handleInvoiceChange = (id, field, value) => {
        setInvoiceForm(prev => ({
            ...prev,
            [id]: {
                ...prev[id],
                [field]: value
            }
        }))
    }

    const handleNarrationChange = (pickListId, lineId, value) => {
        setEditedNarrations(prev => ({
            ...prev,
            [pickListId]: {
                ...(prev[pickListId] || {}),
                [lineId]: value
            }
        }))
    }

    const getLineNarration = (pickListId, lineId, fallback = '') => {
        const edited = editedNarrations[pickListId]?.[lineId]
        return edited !== undefined ? edited : fallback
    }

    const buildPickListLineItemsPayload = (pl, pickListFormId) => {
        const lineItems = Array.isArray(pl.lineItems) ? pl.lineItems : []
        return lineItems.map((li, idx) => {
            const lineId = String(li._id ?? `line-${idx}`)
            return {
                _id: li._id,
                model: li.model?._id ?? li.model,
                quantity: Number(li.quantity),
                value: Number(li.value ?? 0),
                gst: Number(li.gst ?? 18),
                narration: getLineNarration(pickListFormId, lineId, li.narration ?? '') || '',
                listPrice: Number(li.listPrice ?? 0),
                discount: Number(li.discount ?? 0),
                rate: Number(li.rate ?? 0),
                quotationPrice: Number(li.quotationPrice ?? li.rate ?? 0),
                standardLeadTime: li.standardLeadTime,
                committedDeliveryDate: li.committedDeliveryDate
            }
        })
    }

    const updatePickListNarrations = async (pickList, pickListFormId) => {
        const focPlId = pickList._id ?? pickList.id
        const payload = {
            boxNumber: pickList.boxNumber ?? '',
            store: pickList.store?._id ?? pickList.store,
            lineItems: buildPickListLineItemsPayload(pickList, pickListFormId)
        }

        const res = await fetch(`${API_URL}foc/pick-list/${focPlId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        })

        const result = await res.json()
        if (!res.ok || result.error === true) {
            throw new Error(result.message || 'Failed to update pack list narrations')
        }

        setPackListData(prev =>
            prev.map(pl => {
                if (String(pl._id ?? pl.id) !== String(focPlId)) return pl
                const updatedLineItems = buildPickListLineItemsPayload(pickList, pickListFormId)
                return {
                    ...pl,
                    lineItems: (pl.lineItems || []).map((li, idx) => ({
                        ...li,
                        narration: updatedLineItems[idx]?.narration ?? li.narration
                    }))
                }
            })
        )

        return result
    }

    useEffect(() => {
        const fetchPackLists = async () => {
            setLoading(true)
            setError(null)
            try {
                const res = await fetch(`${API_URL}foc/pick-list?pagination=false`, {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                })

                if (!res.ok) {
                    const txt = await res.text()
                    throw new Error(txt || `Status ${res.status}`)
                }

                const json = await res.json()
                const allDocs = json?.data?.docs || []

                // Filter by store if storeid is provided
                const filtered = storeid
                    ? allDocs.filter(pl => {
                        const plStoreId = pl.store?._id || pl.store
                        return String(plStoreId) === String(storeid)
                    })
                    : allDocs

                setPackListData(filtered)
            } catch (err) {
                console.error('Fetch FOC pick-lists error:', err)
                setError(err.message || 'Failed to fetch pack lists')
                setPackListData([])
            } finally {
                setLoading(false)
            }
        }

        fetchPackLists()
    }, [token, storeid])

    const updateStatus = async (e, pl_id, status) => {
        e.preventDefault()
        const url = `${API_URL}foc/pick-list/${pl_id}`
        try {
            const payload = { approvalStatus: status }

            // Add rejection reason if status is rejected
            if (status === 'rejected') {
                payload.rejectionReason = rejectionReason
            }

            const res = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            })
            const result = await res.json()

            if (result.error === false || res.ok) {
                // ⭐ If rejected, ADD BACK the DI quantities
                if (status === 'rejected') {
                    const pickList = packListData.find(
                        pl => pl._id === pl_id || pl.id === pl_id
                    )
                    const deliveryInstructionId = pickList?.deliveryInstruction?._id

                    if (deliveryInstructionId && pickList?.lineItems) {
                        try {
                            // 1️⃣ Fetch the current DI data
                            const diGetRes = await fetch(
                                `${API_URL}foc/delivery-instruction/${deliveryInstructionId}`,
                                {
                                    method: 'GET',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        Authorization: `Bearer ${token}`
                                    }
                                }
                            )

                            if (!diGetRes.ok) {
                                throw new Error(`Failed to fetch DI: ${diGetRes.status}`)
                            }

                            const diGetResult = await diGetRes.json()
                            const currentDI = diGetResult?.data ?? diGetResult

                            // 2️⃣ Build a map of quantities to ADD BACK from rejected picklist
                            const modelAddBack = {}
                            pickList.lineItems.forEach(plItem => {
                                const modelId =
                                    plItem?.model?._id ??
                                    (typeof plItem?.model === 'string' ? plItem.model : null)
                                if (modelId) {
                                    modelAddBack[String(modelId)] =
                                        (modelAddBack[String(modelId)] || 0) +
                                        Number(plItem.quantity || 0)
                                }
                            })

                            // 3️⃣ Update DI line items by ADDING BACK the rejected quantities
                            const updatedDILineItems = currentDI.lineItems.map(li => {
                                const modelId =
                                    li?.model?._id ??
                                    (typeof li?.model === 'string' ? li.model : null)

                                const currentQty = Number(li?.quantity ?? 0)
                                const addBack = modelAddBack[String(modelId)] || 0
                                const newQty = currentQty + addBack

                                return {
                                    _id: li._id,
                                    model: modelId,
                                    quantity: newQty,
                                    listPrice: Number(li?.listPrice ?? 0),
                                    discount: Number(li?.discount ?? 0),
                                    rate: Number(li?.rate ?? 0),
                                    standardLeadTime: li?.standardLeadTime ?? null,
                                    committedDeliveryDate: li?.committedDeliveryDate ?? null
                                }
                            })

                            // 4️⃣ PATCH the DI with updated quantities
                            const diPatchRes = await fetch(
                                `${API_URL}foc/delivery-instruction/${deliveryInstructionId}`,
                                {
                                    method: 'PUT',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        Authorization: `Bearer ${token}`
                                    },
                                    body: JSON.stringify({ lineItems: updatedDILineItems })
                                }
                            )

                            if (diPatchRes.ok) {
                                alert(`Pack list is ${status} and DI quantities reverted successfully`)
                            } else {
                                alert(`Pack list is ${status} but DI update may have failed`)
                            }
                        } catch (diError) {
                            console.error('❌ Error reverting DI:', diError)
                            alert(`Pack list is ${status} but failed to revert DI quantities: ${diError.message}`)
                        }
                    } else {
                        alert(`Pack list is ${status}`)
                    }
                } else {
                    // Not rejected, just approved
                    alert(`Pack list is ${status}`)
                }

                handleCloseDeclineModal()
                // Reload to see updated data
                window.location.reload()
            } else {
                alert('Failed to update Pack List')
            }
        } catch (e) {
            console.error('❌ Update status error:', e)
            alert('Oops!! Error occurred while updating pack list: ' + e.message)
        }
    }

    // Initialize invoice form with pre-filled data
    useEffect(() => {
        if (!Array.isArray(packListData) || packListData.length === 0) return

        setInvoiceForm(prev => {
            const next = { ...prev }

            packListData.forEach((pl, idx) => {
                const id = getPickListId(pl, idx)

                // Don't overwrite an existing form (user may have edited)
                if (next[id]) return

                const client = pl?.deliveryInstruction?.salesOrder?.client || {}
                const addr0 = Array.isArray(client.address)
                    ? client.address[0] || {}
                    : {}

                const gstNumber = client?.gstNumber || ''

                next[id] = {
                    supplyType: 'B2B',
                    buyerGstin: gstNumber || '',
                    buyerPos: getBuyerPos(gstNumber) || '',
                    buyerLocation: addr0.city || addr0.district || '',
                    buyerPin: addr0.pincode || '',
                    shipToGstin: gstNumber || '',
                    shipToLegalName: client?.companyName || '',
                    shipToAddress: addr0.addressLine1 || '',
                    shipToLocation: addr0.city || addr0.district || '',
                    shipToPin: addr0.pincode || '',
                    shipToStateCode: getBuyerPos(gstNumber) || '',
                    vehicleNumber: '',
                    // New dispatch fields
                    dispatchDocumentNo: '',
                    dispatchedThrough: '',
                    otherReferences: '',
                    // Export-specific fields
                    destinationCountryCode: '',
                    countryOfOrigin: 'India',
                    countryOfDestination: '',
                    portOfLoading: '',
                    portOfDischarge: '',
                    termsOfDelivery: '',
                    letterOfCreditNo: '',
                    letterOfCreditDate: '',
                    amendmentNo: '',
                    amendmentDate: '',
                    insuranceCoverNoteNo: '',
                    piNo: '',
                    piDate: '',
                    termsOfPayment: '',
                    marking: '',
                    noOfPackages: '',
                    grossWeight: '',
                    netWeight: ''
                }
            })

            return next
        })
    }, [packListData])

    const toggleAccordion = id => {
        setOpenId(prev => (prev === id ? '' : id))
    }

    const formatDateTime = iso => {
        if (!iso) return '-'
        try {
            const d = new Date(iso)
            return d.toLocaleString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            })
        } catch {
            return iso
        }
    }

    const renderLines = (lines, id, canEditNarration = false) => {
        if (!Array.isArray(lines) || lines.length === 0) {
            return (
                <tr>
                    <td colSpan='7' className='text-center py-3'>
                        No line items
                    </td>
                </tr>
            )
        }

        return lines.map((li, i) => {
            const qty = Number(li?.quantity || 0)
            const value = Number(li?.value || 0)
            const lineId = String(li._id ?? `line-${i}`)
            const narration = getLineNarration(id, lineId, li?.narration ?? '')
            return (
                <tr key={li._id ?? `${id}-li-${i}`}>
                    <td>{i + 1}</td>
                    <td>
                        {li?.model?.modelName ?? '-'}
                        {canEditNarration ? (
                            <input
                                type='text'
                                className='form-control form-control-sm mt-1'
                                placeholder='Enter narration'
                                value={narration}
                                onChange={e => handleNarrationChange(id, lineId, e.target.value)}
                            />
                        ) : (
                            narration && (
                                <>
                                    <br />({narration})
                                </>
                            )
                        )}
                    </td>
                    <td>{li?.model?.partCode ?? '-'}</td>
                    <td>{li?.model?.uom ?? '-'}</td>
                    <td>{qty}</td>
                    <td>{money(value)}</td>
                    <td>{li?.gst ?? 0}%</td>
                </tr>
            )
        })
    }

    // Create invoice using FOC invoice API
    const createInvoice = async packList => {
        if (!token) {
            alert('Missing auth token')
            return
        }

        const id = String(packList._id ?? packList.id ?? '')
        if (!id) {
            alert('Invalid pick list id')
            return
        }

        const form = invoiceForm[id] || {}

        // Validation for mandatory fields
        if (!form.supplyType) {
            alert('Please select Supply Type')
            return
        }
        if (!form.buyerGstin) {
            alert('Please enter Buyer GSTIN')
            return
        }
        if (!form.dispatchDocumentNo) {
            alert('Please enter Dispatch Document No')
            return
        }
        if (!form.dispatchedThrough) {
            alert('Please enter Dispatched Through')
            return
        }

        const payload = {
            pickListId: packList._id ?? packList.id,
            isProformaInvoice: false,
            supplyType: form.supplyType,
            buyerGstin: form.buyerGstin,
            buyerPos: form.buyerPos || getBuyerPos(form.buyerGstin),
            buyerLocation: form.buyerLocation || '',
            buyerPin: form.buyerPin || '',
            shipToGstin: form.shipToGstin || '',
            shipToLegalName: form.shipToLegalName || '',
            shipToAddress: form.shipToAddress || '',
            shipToLocation: form.shipToLocation || '',
            shipToPin: form.shipToPin || '',
            shipToStateCode: form.shipToStateCode || '',
            vehicleNumber: form.vehicleNumber || '',
            // New dispatch fields
            dispatchDocumentNo: form.dispatchDocumentNo || '',
            dispatchedThrough: form.dispatchedThrough || '',
            otherReferences: form.otherReferences || ''
        }

        // Add export-specific fields if supply type is EXPWP or EXPWOP
        if (form.supplyType === 'EXPWP' || form.supplyType === 'EXPWOP') {
            payload.destinationCountryCode = form.destinationCountryCode || ''
            payload.countryOfOrigin = form.countryOfOrigin || 'India'
            payload.countryOfDestination = form.countryOfDestination || ''
            payload.portOfLoading = form.portOfLoading || ''
            payload.portOfDischarge = form.portOfDischarge || ''
            payload.termsOfDelivery = form.termsOfDelivery || ''
            payload.letterOfCreditNo = form.letterOfCreditNo || ''
            payload.letterOfCreditDate = form.letterOfCreditDate
                ? new Date(form.letterOfCreditDate).toISOString()
                : ''
            payload.amendmentNo = form.amendmentNo || ''
            payload.amendmentDate = form.amendmentDate
                ? new Date(form.amendmentDate).toISOString()
                : ''
            payload.insuranceCoverNoteNo = form.insuranceCoverNoteNo || ''
            payload.piNo = form.piNo || ''
            payload.piDate = form.piDate
                ? new Date(form.piDate).toISOString()
                : ''
            payload.termsOfPayment = form.termsOfPayment || ''
            payload.marking = form.marking || ''
            payload.noOfPackages = form.noOfPackages || ''
            payload.grossWeight = form.grossWeight || ''
            payload.netWeight = form.netWeight || ''
        }

        setBtnLoading(true)
        setCreatingId(id)

        try {
            await updatePickListNarrations(packList, id)

            const url = `${API_URL}foc/invoice`

            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload),
                redirect: 'follow'
            })

            const text = await res.text()
            let parsed
            try {
                parsed = JSON.parse(text)
            } catch {
                parsed = text
            }

            if (!res.ok) {
                console.error('Create FOC invoice error response:', parsed)
                alert(
                    typeof parsed === 'string' ? parsed : JSON.stringify(parsed, null, 2)
                )
                return
            }

            console.log('FOC Invoice created:', parsed)
            alert('FOC Invoice created successfully!')
            window.location.reload()
        } catch (err) {
            console.error('Create FOC invoice error:', err)
            alert(err.message || String(err))
        } finally {
            setBtnLoading(false)
            setCreatingId(null)
        }
    }

    if (loading) {
        return (
            <div className='card shadow-sm'>
                <CardBody className='p-4 text-center'>Loading FOC pack lists...</CardBody>
            </div>
        )
    }

    if (error) {
        return (
            <div className='card shadow-sm'>
                <CardBody className='p-4'>
                    <div className='alert alert-danger'>Error: {error}</div>
                </CardBody>
            </div>
        )
    }

    if (!packListData || packListData.length === 0) {
        return (
            <div className='card shadow-sm'>
                <CardBody className='p-4 text-center'>No FOC pack lists found.</CardBody>
            </div>
        )
    }

    const itemsPerPage = 10
    const totalPages = Math.ceil(packListData.length / itemsPerPage) || 1
    const nextPage = pageNumber < totalPages ? pageNumber + 1 : null
    const prevPage = pageNumber > 1 ? pageNumber - 1 : null

    const paginatedList = packListData.slice((pageNumber - 1) * itemsPerPage, pageNumber * itemsPerPage)

    const getPaginationItems = () => {
        if (totalPages <= 4) {
            return Array.from({ length: totalPages }, (_, i) => i + 1)
        }
        return [1, 2, 3, '...', totalPages]
    }

    return (
        <div className='card shadow-sm'>
            <CardBody className='p-4'>
                <div className='d-flex justify-content-between align-items-center mb-3'>
                    <h5 className='mb-0 ml-1'>FOC Pack Lists</h5>
                </div>

                <Accordion
                    open={openId || ''}
                    toggle={toggleAccordion}
                    id='focInvoiceAccordion'
                >
                    {paginatedList.map((pl, idx) => {
                        const id = getPickListId(pl, idx)

                        const boxNumber = pl.boxNumber ?? pl.box ?? '-'
                        const salesOrderNumber = pl?.salesOrderNumber ??
                            pl?.deliveryInstruction?.salesOrderNumber ??
                            pl?.deliveryInstruction?.salesOrder?.salesOrderNumber ?? '-'
                        const diNumber = pl?.deliveryInstruction?.deliveryInstructionId ?? '-'
                        const createdAt = pl.createdAt ?? pl.updatedAt ?? null

                        const lineItems = Array.isArray(pl.lineItems) ? pl.lineItems : []
                        const form = invoiceForm[id] || {}

                        const status = pl.approvalStatus || 'pending'
                        const isApproved = status === 'approved'
                        const isRejected = status === 'rejected'
                        const isPending = status === 'pending'
                        const canEditNarration = status !== 'invoiced' && status !== 'rejected'

                        return (
                            <AccordionItem key={id}>
                                <AccordionHeader targetId={id}>
                                    <div className='d-flex flex-column flex-md-row align-items-start gap-2 w-100'>
                                        <div style={{ minWidth: 220 }}>
                                            <strong>
                                                {salesOrderNumber} | {diNumber} |{' '}
                                                <span className='text-muted small'>
                                                    Box: {boxNumber}
                                                </span>{' '}
                                                |{' '}
                                                <span
                                                    className={
                                                        isApproved
                                                            ? 'text-success'
                                                            : isRejected
                                                                ? 'text-danger'
                                                                : 'text-primary'
                                                    }
                                                >
                                                    {status.toUpperCase()}
                                                </span>
                                            </strong>
                                        </div>
                                    </div>
                                </AccordionHeader>

                                <AccordionBody accordionId={id}>
                                    {/* Rejection reason */}
                                    {isRejected && pl.reasonForRejection && (
                                        <div className='alert alert-danger mb-3'>
                                            <strong>Rejection Reason:</strong> {pl.reasonForRejection}
                                        </div>
                                    )}

                                    {/* Invoice Input Fields (only for approved) - MOVED TO TOP */}
                                    {isApproved && (
                                        <div className='mb-4'>
                                            <div className='row g-3'>
                                                <div className='col-md-4'>
                                                    <label className='form-label mb-1'>
                                                        Supply Type <span className='text-danger'>*</span>
                                                    </label>
                                                    <select
                                                        className='form-select form-select-sm'
                                                        value={form.supplyType || 'B2B'}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'supplyType', e.target.value)
                                                        }
                                                    >
                                                        <option value='B2B'>B2B</option>
                                                        <option value='SEZWP'>SEZWP</option>
                                                        <option value='SEZWOP'>SEZWOP</option>
                                                        <option value='EXPWP'>EXPWP</option>
                                                        <option value='EXPWOP'>EXPWOP</option>
                                                        <option value='DEXP'>DEXP</option>
                                                    </select>
                                                </div>

                                                <div className='col-md-4'>
                                                    <label className='form-label mb-1'>
                                                        Buyer GSTIN <span className='text-danger'>*</span>
                                                    </label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        value={form.buyerGstin || ''}
                                                        onChange={e => {
                                                            const val = e.target.value
                                                            handleInvoiceChange(id, 'buyerGstin', val)
                                                            handleInvoiceChange(id, 'buyerPos', getBuyerPos(val))
                                                        }}
                                                    />
                                                </div>

                                                <div className='col-md-4'>
                                                    <label className='form-label mb-1'>Buyer POS <span className='text-danger'>*</span></label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        value={form.buyerPos || ''}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'buyerPos', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className='col-md-6'>
                                                    <label className='form-label mb-1'>Buyer Location <span className='text-danger'>*</span></label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        value={form.buyerLocation || ''}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'buyerLocation', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className='col-md-6'>
                                                    <label className='form-label mb-1'>Buyer Pincode <span className='text-danger'>*</span></label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        value={form.buyerPin || ''}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'buyerPin', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className='col-md-6'>
                                                    <label className='form-label mb-1'>Ship To GSTIN <span className='text-danger'>*</span></label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        value={form.shipToGstin || ''}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'shipToGstin', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className='col-md-6'>
                                                    <label className='form-label mb-1'>Ship To Legal Name <span className='text-danger'>*</span></label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        value={form.shipToLegalName || ''}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'shipToLegalName', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className='col-md-6'>
                                                    <label className='form-label mb-1'>Ship To Address <span className='text-danger'>*</span></label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        value={form.shipToAddress || ''}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'shipToAddress', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className='col-md-6'>
                                                    <label className='form-label mb-1'>Ship To Location <span className='text-danger'>*</span></label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        value={form.shipToLocation || ''}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'shipToLocation', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className='col-md-4'>
                                                    <label className='form-label mb-1'>Ship To PIN <span className='text-danger'>*</span></label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        value={form.shipToPin || ''}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'shipToPin', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className='col-md-4'>
                                                    <label className='form-label mb-1'>Ship To State Code <span className='text-danger'>*</span></label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        maxLength={2}
                                                        value={form.shipToStateCode || ''}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'shipToStateCode', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className='col-md-4'>
                                                    <label className='form-label mb-1'>Vehicle Number <span className='text-danger'>*</span></label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        value={form.vehicleNumber || ''}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'vehicleNumber', e.target.value)
                                                        }
                                                    />
                                                </div>

                                                <div className='col-md-4'>
                                                    <label className='form-label mb-1'>
                                                        Dispatch Document No <span className='text-danger'>*</span>
                                                    </label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        value={form.dispatchDocumentNo || ''}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'dispatchDocumentNo', e.target.value)
                                                        }
                                                        placeholder='Enter dispatch document number'
                                                    />
                                                </div>

                                                <div className='col-md-4'>
                                                    <label className='form-label mb-1'>
                                                        Dispatched Through <span className='text-danger'>*</span>
                                                    </label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        value={form.dispatchedThrough || ''}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'dispatchedThrough', e.target.value)
                                                        }
                                                        placeholder='Enter dispatch method'
                                                    />
                                                </div>

                                                <div className='col-md-4'>
                                                    <label className='form-label mb-1'>
                                                        Other References
                                                    </label>
                                                    <input
                                                        type='text'
                                                        className='form-control form-control-sm'
                                                        value={form.otherReferences || ''}
                                                        onChange={e =>
                                                            handleInvoiceChange(id, 'otherReferences', e.target.value)
                                                        }
                                                        placeholder='Optional references'
                                                    />
                                                </div>
                                            </div>

                                            {/* Export-specific fields */}
                                            {(form.supplyType === 'EXPWP' || form.supplyType === 'EXPWOP') && (
                                                <>
                                                    <h6 className='mt-4 mb-3'>Export Details</h6>
                                                    <div className='row g-3'>
                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>
                                                                Destination Country Code <span className='text-danger'>*</span>
                                                            </label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.destinationCountryCode || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'destinationCountryCode', e.target.value)
                                                                }
                                                                placeholder='e.g., US'
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Country of Origin</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.countryOfOrigin || 'India'}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'countryOfOrigin', e.target.value)
                                                                }
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Country of Destination</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.countryOfDestination || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'countryOfDestination', e.target.value)
                                                                }
                                                                placeholder='e.g., United States'
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Port of Loading</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.portOfLoading || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'portOfLoading', e.target.value)
                                                                }
                                                                placeholder='e.g., Chennai'
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Port of Discharge</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.portOfDischarge || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'portOfDischarge', e.target.value)
                                                                }
                                                                placeholder='e.g., New York'
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Terms of Delivery</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.termsOfDelivery || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'termsOfDelivery', e.target.value)
                                                                }
                                                                placeholder='e.g., CIF'
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Letter of Credit No</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.letterOfCreditNo || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'letterOfCreditNo', e.target.value)
                                                                }
                                                                placeholder='e.g., LC123456789'
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Letter of Credit Date</label>
                                                            <input
                                                                type='date'
                                                                className='form-control form-control-sm'
                                                                value={form.letterOfCreditDate || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'letterOfCreditDate', e.target.value)
                                                                }
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Amendment No</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.amendmentNo || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'amendmentNo', e.target.value)
                                                                }
                                                                placeholder='e.g., AMD001'
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Amendment Date</label>
                                                            <input
                                                                type='date'
                                                                className='form-control form-control-sm'
                                                                value={form.amendmentDate || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'amendmentDate', e.target.value)
                                                                }
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Insurance Cover Note No</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.insuranceCoverNoteNo || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'insuranceCoverNoteNo', e.target.value)
                                                                }
                                                                placeholder='e.g., INS-COVER-001'
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>PI No</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.piNo || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'piNo', e.target.value)
                                                                }
                                                                placeholder='e.g., PI/2023-24/001'
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>PI Date</label>
                                                            <input
                                                                type='date'
                                                                className='form-control form-control-sm'
                                                                value={form.piDate || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'piDate', e.target.value)
                                                                }
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Terms of Payment</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.termsOfPayment || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'termsOfPayment', e.target.value)
                                                                }
                                                                placeholder='e.g., Net 30 Days'
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Marking</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.marking || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'marking', e.target.value)
                                                                }
                                                                placeholder='e.g., Fragile'
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>No of Packages</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.noOfPackages || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'noOfPackages', e.target.value)
                                                                }
                                                                placeholder='e.g., 10 Boxes'
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Gross Weight</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.grossWeight || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'grossWeight', e.target.value)
                                                                }
                                                                placeholder='e.g., 500 KG'
                                                            />
                                                        </div>

                                                        <div className='col-md-4'>
                                                            <label className='form-label mb-1'>Net Weight</label>
                                                            <input
                                                                type='text'
                                                                className='form-control form-control-sm'
                                                                value={form.netWeight || ''}
                                                                onChange={e =>
                                                                    handleInvoiceChange(id, 'netWeight', e.target.value)
                                                                }
                                                                placeholder='e.g., 450 KG'
                                                            />
                                                        </div>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* Line Items Table */}
                                    <div className='table-responsive mb-3'>
                                        <Table striped bordered size='sm'>
                                            <thead>
                                                <tr>
                                                    <th>SI</th>
                                                    <th>Name</th>
                                                    <th>Part Code</th>
                                                    <th>UOM</th>
                                                    <th>Qty</th>
                                                    <th>Value</th>
                                                    <th>GST</th>
                                                </tr>
                                            </thead>
                                            <tbody>{renderLines(lineItems, id, canEditNarration)}</tbody>
                                        </Table>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className='d-flex justify-content-end gap-2 mt-3'>
                                        {isPending && (
                                            <>
                                                <Button
                                                    color='success'
                                                    size='sm'
                                                    onClick={e => updateStatus(e, pl._id || pl.id, 'approved')}
                                                >
                                                    Approve
                                                </Button>
                                                <Button
                                                    color='danger'
                                                    size='sm'
                                                    outline
                                                    onClick={() => {
                                                        setPlId(pl._id || pl.id)
                                                        setShowDeclineModal(true)
                                                    }}
                                                >
                                                    Reject
                                                </Button>
                                            </>
                                        )}

                                        {isApproved && (
                                            <Button
                                                color='primary'
                                                size='sm'
                                                onClick={() => createInvoice(pl)}
                                                disabled={btnLoading && creatingId === id}
                                            >
                                                {btnLoading && creatingId === id ? 'Creating...' : 'Create Invoice'}
                                            </Button>
                                        )}
                                    </div>
                                </AccordionBody>
                            </AccordionItem>
                        )
                    })}
                </Accordion>

                {/* Pagination: Prev 1 2 3 ... n Next */}
                {totalPages > 1 && (
                    <div className='d-flex justify-content-end mt-4'>
                        <nav aria-label='...'>
                            <ul className='pagination'>
                                <li className={`page-item ${!prevPage ? 'disabled' : ''}`}>
                                    <button
                                        className='page-link text-primary'
                                        onClick={() => prevPage && setPageNumber(prevPage)}
                                    >
                                        Previous
                                    </button>
                                </li>

                                {getPaginationItems().map((item, i) =>
                                    item === '...' ? (
                                        <li key={`dots-${i}`} className='page-item disabled'>
                                            <span className='page-link'>...</span>
                                        </li>
                                    ) : (
                                        <li
                                            key={item}
                                            className={`page-item ${pageNumber === item ? 'active' : ''}`}
                                        >
                                            <button
                                                className={`page-link ${pageNumber === item ? 'text-white' : 'text-primary'}  `}
                                                onClick={() => setPageNumber(item)}
                                            >
                                                {item}
                                            </button>
                                        </li>
                                    )
                                )}

                                <li className={`page-item ${!nextPage ? 'disabled' : ''}`}>
                                    <button
                                        className='page-link text-primary'
                                        onClick={() => nextPage && setPageNumber(nextPage)}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                )}

                {/* Decline Modal */}
                <Modal isOpen={showDeclineModal} toggle={handleCloseDeclineModal}>
                    <ModalHeader toggle={handleCloseDeclineModal}>Reject Pack List</ModalHeader>
                    <ModalBody>
                        <Label>Reason for Rejection</Label>
                        <Input
                            type='textarea'
                            rows={4}
                            value={rejectionReason}
                            onChange={e => setRejectionReason(e.target.value)}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button color='secondary' onClick={handleCloseDeclineModal}>
                            Cancel
                        </Button>
                        <Button
                            color='danger'
                            onClick={e => updateStatus(e, plId, 'rejected')}
                            disabled={!rejectionReason.trim()}
                        >
                            Reject
                        </Button>
                    </ModalFooter>
                </Modal>
            </CardBody>
        </div>
    )
}

export default FocInvoice
